import { useEffect } from "react";
import { Link } from "react-router-dom";
import filmsData, { films } from "@/mocks/films";
import { siteStats } from "@/mocks/collections";
import { sortFilms } from "@/utils/filmQuery";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/base/SectionHeading";
import RatingBadge from "@/components/base/RatingBadge";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import GenreGrid from "@/pages/home/components/GenreGrid";
import CollectionBlock from "@/pages/home/components/CollectionBlock";

export default function Home() {
  const featured = films.filter((film) => film.featured);
  const slides = featured.length > 0 ? featured.slice(0, 4) : films.slice(0, 3);
  const latest = sortFilms(films, "newest").slice(0, 5);
  const ranking = sortFilms(films, "rating").slice(0, 5);

  useEffect(() => {
    document.title = "色即是空 - 经典影视作品详情与剧情介绍";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "色即是空影视内容站提供该影片的详细剧情介绍、演员信息及相关影视资源，帮助用户全面了解这部经典作品。",
      );
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute("content", "色即是空,影视作品,色即是空剧情,色即是空演员,色即是空影评");
  }, []);

  return (
    <>
      <HeroCarousel slides={slides} />
      <GenreGrid />

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="Latest"
            title="最新上线影视"
            description="近期补充与更新资料的作品，卡片信息与详情页资料保持完全一致。"
            action={
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-xs" />
                </span>
              </Link>
            }
          />

          {latest.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
              <p className="font-heading text-lg text-foreground-800">暂无新增影视作品</p>
              <p className="mt-2 text-sm text-foreground-500">
                资料库正在整理中，你可以先浏览已有的分类内容。
              </p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {latest.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="w-full bg-background-100/70">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Archive"
                title="关于这座影史资料库"
                description="我们把作品资料拆成可查证的信息层：基本档案、剧情脉络、主创演员、相关作品。每一项都来自可追溯的条目，不做猜测式补全。"
              />
              <div className="mt-6 space-y-4 text-sm leading-[1.9] text-foreground-700">
                <p>
                  「色即是空」最初以一部校园爱情喜剧被许多观众记住。二十多年过去，它留下的不只是笑点，
                  还有那种在狼狈里仍然愿意认真喜欢一个人的笨拙。我们以它为起点，把同时代、同母题的经典作品
                  一起收录进来，让单部影片有参照，也有上下文。
                </p>
                <p>
                  站内所有剧情介绍均由编辑整理撰写，不复制第三方简介；演员与主创信息以作品中出现的署名为主。
                  若你发现资料有误，欢迎通过关于页的反馈通道联系我们。
                </p>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-information-line" />
                  </span>
                  了解站点定位
                </Link>
                <Link
                  to="/category?genre=剧情"
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  从剧情片开始
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">
                    高分口碑榜
                  </h3>
                  <span className="text-[11px] uppercase tracking-[0.16em] text-foreground-400">
                    Top Rated
                  </span>
                </div>
                <ul className="divide-y divide-background-200/70">
                  {ranking.map((film, index) => (
                    <li key={film.id}>
                      <Link
                        to={`/detail/${film.id}`}
                        className="flex items-center gap-3 py-3 transition-colors hover:text-primary-700"
                      >
                        <span
                          className={`w-5 font-label text-sm font-bold ${
                            index === 0 ? "text-accent-600" : "text-background-500"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground-900">
                          {film.title}
                        </span>
                        <span className="whitespace-nowrap text-xs text-foreground-500">
                          {film.year}
                        </span>
                        <RatingBadge rating={film.rating} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-5"
              >
                <p className="font-heading text-2xl font-bold text-primary-700 md:text-[26px]">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CollectionBlock />

      <section className="w-full bg-background-50">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start justify-between gap-5 px-4 py-12 md:flex-row md:items-center md:px-8 md:py-14">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary-600">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-eye-line" />
              </span>
              资料检索
            </p>
            <h3 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              想找某位演员的旧作？直接搜名字。
            </h3>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              搜索支持片名、原名、导演、演员姓名、角色名与类型标签，结果页可以继续筛选年份与地区。
            </p>
          </div>
          <Link
            to="/search"
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-search-line" />
            </span>
            进入检索页
          </Link>
        </div>
      </section>
    </>
  );
}